import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowLeft, Camera, CheckCircle2, Loader2, ShieldCheck, UploadCloud, RotateCcw, RotateCw } from 'lucide-react';
import API from '../../utils/apiBase';
import {
  captureStaffInvitation, clearPendingStaffInvitation,
  pendingStaffInvitation, staffInvitationSignInPath,
} from '../../lib/staffIntroductionInvitation';

const HEADERS = () => ({ Authorization: `Bearer ${localStorage.getItem('workerToken') || ''}` });

export default function WorkerStaffIntroductionPage() {
  const [record, setRecord] = useState(null);
  const [summary, setSummary] = useState('');
  const [consent, setConsent] = useState(false);
  const [photo, setPhoto] = useState('');
  const [photoVersion, setPhotoVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [complete, setComplete] = useState(false);
  const [signInNeeded, setSignInNeeded] = useState(false);
  const [invitation, setInvitation] = useState(() => pendingStaffInvitation());
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const previewRef = useRef(null);

  const refresh = async () => {
    const { data } = await axios.get(`${API}/worker/staff-introduction`, { headers: HEADERS() });
    setRecord(data);
    setConsent(false); // Every current request requires affirmative, recipient-specific consent.
    setSummary(data.summary || '');
    return data;
  };

  useEffect(() => {
    let active = true;
    // Capture the invitation BEFORE checking authentication. Magic-link login
    // opens another tab, and the token must never be put in URL query strings.
    const pending = captureStaffInvitation();
    setInvitation(pending);
    if (new URLSearchParams(window.location.hash.slice(1)).has('invite')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    const workerToken = localStorage.getItem('workerToken');
    const loginSlug = localStorage.getItem('workerWorkspaceSlug');
    if (!workerToken || (pending && loginSlug && loginSlug !== pending.workspaceSlug)) {
      setSignInNeeded(true);
      setLoading(false);
      return () => { active = false; };
    }
    const load = async () => {
      if (pending) {
        await axios.post(`${API}/worker/staff-introduction/redeem`, { token: pending.token }, {
          headers: HEADERS(), timeout: 20000,
        });
        clearPendingStaffInvitation();
        if (active) setInvitation(null);
      }
      return refresh();
    };
    load().catch((e) => {
      if (!active) return;
      const status = e.response?.status;
      if (status === 401) {
        setSignInNeeded(true);
        setError('Your applicant/worker session has expired. Sign in using your own email to continue this invitation.');
      } else if (status === 403) {
        // Applicant-only records may be held separately from an authorised staff
        // record. Repeated sign-in does not grant staff access or fix this.
        setError('You are signed in, but your agency has not linked this invitation to an eligible staff record for your account. Ask your agency to check your staff profile and application status.');
      } else if (status === 404) {
        setError('This invitation may already be used, expired, or linked to another applicant/worker. Check you used the correct account or ask your agency for a fresh invitation.');
      } else {
        setError(typeof e.response?.data?.detail === 'string'
          ? e.response.data.detail
          : 'The staff profile could not be opened. Contact your agency before retrying.');
      }
    }).finally(() => { if (active) setLoading(false); });
    return () => {
      active = false;
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    };
  }, []);

  useEffect(() => {
    if (!record?.has_photo || !localStorage.getItem('workerToken')) return;
    let alive = true;
    axios.get(`${API}/worker/staff-introduction/photo`, { headers: HEADERS(), responseType: 'blob' })
      .then(({ data }) => {
        if (!alive) return;
        const objectUrl = URL.createObjectURL(data);
        if (previewRef.current) URL.revokeObjectURL(previewRef.current);
        previewRef.current = objectUrl;
        setPhoto(objectUrl);
      }).catch(() => { /* The text form remains usable if preview is temporarily unavailable. */ });
    return () => { alive = false; };
  }, [record?.has_photo, photoVersion]);

  const upload = async (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024) {
      setError('Please use a JPG, PNG or WebP photo smaller than 5 MB.');
      return;
    }
    setBusy('photo');
    setError('');
    try {
      const body = new FormData();
      body.append('file', file);
      await axios.post(`${API}/worker/staff-introduction/photo`, body, {
        headers: HEADERS(), timeout: 60000,
      });
      setPhotoVersion(value => value + 1);
      await refresh();
    } catch (e) {
      setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not save the photograph securely. Please try again.');
    } finally { setBusy(''); }
  };

  const rotate = async (direction) => {
    if (!record?.has_photo || busy) return;
    setBusy('rotate');
    setError('');
    try {
      await axios.post(`${API}/worker/staff-introduction/photo/rotate`, { direction }, {
        headers: HEADERS(), timeout: 60000,
      });
      setPhotoVersion(value => value + 1);
      await refresh();
    } catch (e) {
      setError(typeof e.response?.data?.detail === 'string'
        ? e.response.data.detail : 'Could not rotate your photograph. Please ask your agency for help.');
    } finally { setBusy(''); }
  };

  const portraitControls = () => (
    <div className="mt-2 flex flex-wrap gap-2" aria-label="Correct portrait orientation">
      <button type="button" disabled={!!busy} onClick={() => rotate('left')}
        className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm disabled:opacity-50">
        <RotateCcw size={16} /> Rotate left
      </button>
      <button type="button" disabled={!!busy} onClick={() => rotate('right')}
        className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm disabled:opacity-50">
        <RotateCw size={16} /> Rotate right
      </button>
    </div>
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!record?.has_photo) { setError('Please add a clear photograph first.'); return; }
    setBusy('submit'); setError('');
    try {
      await axios.post(`${API}/worker/staff-introduction`, {
        summary: summary.trim(),
        client_org_id: record.client_org_id, request_id: record.request_id,
        consent_to_share: consent,
      }, { headers: HEADERS() });
      setComplete(true);
      await refresh();
    } catch (e) {
      setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Please check the form and try again.');
    } finally { setBusy(''); }
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 text-slate-800">
      <section className="mx-auto max-w-xl rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
        <Link to="/worker/dashboard" className="mb-6 inline-flex items-center gap-2 text-sm text-blue-700">
          <ArrowLeft size={16} /> Back to my staff portal
        </Link>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-blue-700">
          <ShieldCheck size={18} /> Private staff submission
        </div>
        <h1 className="mt-2 text-2xl font-bold text-slate-900">Your staff profile</h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          A short professional introduction for care homes. This is separate from your Employment Pack.
          You are not being asked to re-upload DBS, Right to Work or identity documents.
        </p>

        {loading && <div className="mt-8 flex items-center gap-2"><Loader2 className="animate-spin" /> Loading your profile…</div>}
        {!loading && signInNeeded && (
          <div className="mt-6 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-slate-800" data-testid="staff-invitation-sign-in">
            <strong>Continue with your own applicant or worker account</strong>
            <p className="mt-2">
              The staff invitation is saved for this sign-in. Use the email address associated with your application.
              After opening the email sign-in link, CareDock will bring you back here automatically.
            </p>
            <Link
              className="mt-3 inline-flex rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white"
              to={staffInvitationSignInPath(invitation)}
              onClick={() => {
                // A portal admin login and another applicant's worker session cannot
                // be used to redeem this identity-bound invitation.
                localStorage.removeItem('workerToken');
                localStorage.removeItem('workerEmployee');
                if (invitation?.workspaceSlug) {
                  localStorage.setItem('workerWorkspaceSlug', invitation.workspaceSlug);
                }
              }}
            >Sign in to continue</Link>
            {!invitation && <p className="mt-3 text-xs text-slate-700">The invitation needs to be opened again from the original link. Ask your agency for a new one if it has expired.</p>}
          </div>
        )}
        {!loading && error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
            <p>{error}</p>
            {!signInNeeded && invitation && (
              <Link
                to={staffInvitationSignInPath(invitation)}
                onClick={() => {
                  localStorage.removeItem('workerToken');
                  localStorage.removeItem('workerEmployee');
                  localStorage.setItem('workerWorkspaceSlug', invitation.workspaceSlug);
                }}
                className="mt-3 inline-flex font-semibold underline"
              >Use a different applicant/worker account</Link>
            )}
          </div>
        )}
        {!loading && record && !record.can_submit && !['pending_review', 'approved'].includes(record.status) && (
          <div className="mt-6 rounded-xl border p-5 text-sm text-slate-700">
            {record.invitation_pending
              ? 'A staff profile has been requested for you. Open your personal invitation link while signed in. An expired or used link must be reissued by your agency.'
              : 'Your agency has not requested a current staff introduction from this account. Contact your agency if you expected a request.'}
          </div>
        )}
        {!loading && record && (record.can_submit || ['pending_review', 'approved'].includes(record.status)) && (
          <>
            <div className="mt-6 rounded-xl bg-blue-50 p-4">
              <p className="text-lg font-bold">{record.name}</p>
              <p className="text-sm">{record.role}</p>
              <p className="mt-1 text-xs text-slate-500">Name and job title are taken from your CareDock staff record.</p>
              <p className="mt-2 text-xs text-slate-600">Prepared for: {record.requested_for || 'Your agency client'}</p>
            </div>
            {complete || record.status === 'pending_review' ? (
              <div className="mt-6 rounded-xl border border-blue-200 bg-blue-50 p-5 text-blue-900">
                <CheckCircle2 className="mb-2" />
                <h2 className="font-bold">Submitted for review</h2>
                <p className="mt-2 text-sm">Your agency will review your photograph and information before sharing this introduction with {record.requested_for}.</p>
                {photo && (
                  <div className="mt-4">
                    <img src={photo} alt="Your submitted staff photograph" className="h-44 w-36 rounded-lg border object-contain bg-white" />
                    <p className="mt-2 text-xs">Photograph sideways? Correct it before manager approval.</p>
                    {portraitControls()}
                  </div>
                )}
              </div>
            ) : record.status === 'approved' ? (
              <div className="mt-6 rounded-xl border border-blue-200 bg-blue-50 p-5 text-blue-900">
                <CheckCircle2 className="mb-2" />
                <h2 className="font-bold">Profile approved by your agency</h2>
                <p className="mt-2 text-sm">Your approved one-page introduction is prepared for {record.requested_for}. A new care home requires a new request and fresh consent.</p>
              </div>
            ) : record.can_submit ? (
              <form onSubmit={submit} className="mt-6 space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-semibold">1. Your photograph</label>
                  <p className="mb-3 text-xs text-slate-600">A recent, clear head-and-shoulders photograph. No filters or ID documents.</p>
                  {photo && (
                    <div className="mb-3">
                      <img src={photo} alt="Your selected staff portrait" className="h-44 w-36 rounded-lg border bg-white object-contain" />
                      <p className="mt-2 text-xs text-slate-500">Check that your head is upright. Rotate the photo here if needed.</p>
                      {portraitControls()}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button type="button" disabled={!!busy} onClick={() => cameraRef.current?.click()}
                      className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
                      <Camera size={17} /> Take photo
                    </button>
                    <button type="button" disabled={!!busy} onClick={() => galleryRef.current?.click()}
                      className="inline-flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold disabled:opacity-50">
                      <UploadCloud size={17} /> Choose photo
                    </button>
                  </div>
                  {busy === 'photo' && <p className="mt-2 text-sm">Saving photograph securely…</p>}
                  <input ref={cameraRef} aria-label="Take your staff portrait" className="hidden" type="file"
                    accept="image/jpeg,image/png,image/webp" capture="user" onChange={e => upload(e.target.files?.[0])} />
                  <input ref={galleryRef} aria-label="Choose your staff portrait" className="hidden" type="file"
                    accept="image/jpeg,image/png,image/webp" onChange={e => upload(e.target.files?.[0])} />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-semibold" htmlFor="summary">2. About you</label>
                  <textarea id="summary" required minLength={30} maxLength={950} rows={6}
                    value={summary} onChange={e => setSummary(e.target.value)}
                    placeholder="In a few sentences, describe how you support residents and the care you provide."
                    className="w-full rounded-xl border px-3 py-3 text-sm" />
                  <p className="mt-1 text-right text-xs text-slate-500">{summary.length}/950</p>
                </div>
                <p className="text-xs leading-relaxed text-slate-600">Your work history, qualifications and verification records stay in CareDock. You do not need to enter them again for this one-page introduction. Your agency reviews your words before sharing them.</p>
                <label className="flex gap-3 rounded-xl border bg-slate-50 p-3 text-sm">
                  <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} required className="mt-1 h-4 w-4" />
                  <span>I confirm this information is accurate and consent to my agency sharing this one-page portrait and professional introduction <strong>only with {record.requested_for}</strong> for a staff introduction. A different care home requires a new request and my fresh consent.</span>
                </label>
                <button disabled={!!busy || !record.has_photo || !consent} type="submit"
                  className="w-full rounded-xl bg-blue-700 px-4 py-3 font-bold text-white disabled:cursor-not-allowed disabled:opacity-50">
                  {busy === 'submit' ? 'Submitting…' : 'Submit for agency approval'}
                </button>
              </form>
            ) : null}
            {record.status === 'changes_requested' && record.review_note && (
              <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm">Correction requested: {record.review_note}</p>
            )}
          </>
        )}
        <p className="mt-8 text-xs text-slate-500">Your full employment and compliance files remain private. Submitting this introduction does not confirm fitness or clearance to work.</p>
      </section>
    </main>
  );
}
